import {
  useWSModalCustom,
  WSForm,
  WSInfoBox,
  WSInputText,
  WSInputTextarea,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  BatchCreate,
  BatchProcessingStrategy,
  PayableImportBatchCreate,
  PayableImportBatchCreateType
} from "@wingspanhq/wingspan-api-sdk";
import { queriesBatches } from "@query";

import { ErrorAlert } from "@components/ErrorAlert";
import { useLocalStorage } from "../../../../shared/hooks/useLocalStorage";
import { useSession } from "../../../../v3/hooks/useSession";
import { IdempotencyKey } from "../../../../v3/utils/idempotencyKey";
import {
  PAYABLE_IMPORT_BATCH_STATUS_CONFIGURATION,
  PayableImportBatchStatus
} from "../../constants/payableImportBatchStatus";

type FormData = {
  name: string;
  notes: string;
  status: PayableImportBatchStatus;
};

type Props = WSModalCustomProps<string | null>;

const STATUS_TOOLTIP = `Payable statuses

                Draft
                  • Payables will be hidden to contractors until it is opened by you or a team member

                Open
                  • Contractors can see payables, but it will not be paid until it is approved by you or a team member

                Approved
                  • Contractors will automatically be paid on the next payroll date

                Paid (historical upload)
                  • Payables will be visible to contractors and marked as paid off of Wingspan
                  • Historical uploads are typically used for recording past payments. Companies looking to file Form 1099s can use this option to include payments paid off of Wingspan for a given filing year.
                `;

const STATUS_OPTIONS = [
  {
    value: PayableImportBatchStatus.Draft,
    label: PayableImportBatchStatus.Draft,
    labels: [
      "Payables will be hidden to contractors until it is opened by you or a team member"
    ]
  },
  {
    value: PayableImportBatchStatus.Open,
    label: PayableImportBatchStatus.Open,
    labels: [
      "Contractors can see payables, but it will not be paid until it is approved by you or a team member"
    ]
  },
  {
    value: PayableImportBatchStatus.Approved,
    label: PayableImportBatchStatus.Approved,
    labels: ["Contractors will automatically be paid on the next payroll date"]
  },
  {
    value: PayableImportBatchStatus.Paid,
    label: PayableImportBatchStatus.Paid,
    labels: [
      "Payables will be visible to contractors and marked as paid off of Wingspan"
    ]
  }
];

/**
 * Name, notes and payable status of a new payables import batch.
 *
 * v1 collected these after creating the batch and saved them with a batch
 * update. v3 accepts `_configuration.payableStatus` at create time only, so the
 * batch is created when this form is submitted rather than before it opens.
 */
const ModalNewPayableBatch: React.FC<Props> = props => {
  const { personId } = useSession();
  const [status, setStatus] = useLocalStorage<PayableImportBatchStatus>(
    `${personId}:bulk-payable-status`,
    PayableImportBatchStatus.Draft
  );
  const [createBatch, createBatchMeta] =
    queriesBatches.createBatch.useMutation();

  const handleSubmit = async (data: FormData) => {
    const name = data.name.trim();
    const notes = data.notes.trim();
    setStatus(data.status);

    const idempotencyKey = new IdempotencyKey({
      type: PayableImportBatchCreateType.PAYABLE_IMPORT,
      name,
      notes,
      status: data.status
    });

    // The SDK generates `BatchCreate` as `{ type: 'PayableImport' } &
    // PayableImportBatchCreate | …`, and intersecting the bare literal with the
    // nominal `PayableImportBatchCreateType` member collapses every property to
    // `never` — so no value satisfies it. Build the real variant type and cast
    // once here, at the SDK boundary.
    const batchCreate: PayableImportBatchCreate = {
      type: PayableImportBatchCreateType.PAYABLE_IMPORT,
      _configuration: {
        processingStrategy: BatchProcessingStrategy.MERGE,
        ...PAYABLE_IMPORT_BATCH_STATUS_CONFIGURATION[data.status]
      },
      ...(name ? { name } : {}),
      ...(notes ? { notes } : {})
    };

    await createBatch(
      {
        idempotencyKey: idempotencyKey.value,
        batchCreate: batchCreate as BatchCreate
      },
      {
        onSuccess: batch => {
          idempotencyKey.destroy();
          props.onClose(batch.id ?? null);
        }
      }
    );
  };

  return (
    <WSForm<FormData>
      defaultValues={{ name: "", notes: "", status }}
      onSubmit={handleSubmit}
    >
      <WSForm.Values names={["status"]}>
        {({ status: selectedStatus }) => (
          <WSModal
            title="New batch"
            size="M"
            onClose={() => props.onClose(null)}
            actions={{
              alignment: "fill",
              sortButtons: false,
              buttons: [
                {
                  destructive: true,
                  kind: "Secondary",
                  type: "button",
                  label: "Cancel",
                  onClick: () => props.onClose(null)
                },
                {
                  label: "Continue",
                  disabled: !selectedStatus,
                  loading: createBatchMeta.isLoading
                }
              ]
            }}
          >
            <WSList gap="XL">
              <WSText.ParagraphSm weight="book" color="gray600">
                A new batch will be created and ready for your formatted
                spreadsheet to be uploaded. Select a payables status for the
                batch. Optionally, add a name and notes to identify the batch.
              </WSText.ParagraphSm>

              <WSForm.Field
                name="name"
                label="Batch name (optional)"
                component={WSInputText}
                componentProps={{
                  placeholder: "Enter name for the import batch"
                }}
              />
              <WSForm.Field
                name="notes"
                label="Notes (optional)"
                component={WSInputTextarea}
                componentProps={{
                  placeholder: "Provide notes for your reference",
                  helperText:
                    "Provide details about the batch for your reference"
                }}
              />

              <WSInfoBox>
                <WSText.ParagraphSm weight="book" color="gray600">
                  Tip: adding a name and notes can be helpful when filtering by
                  batches in the contractor list and help identify the purpose
                  of an imported batch for your own reference.
                </WSText.ParagraphSm>
              </WSInfoBox>

              <WSForm.Field
                name="status"
                label="Select a status for the batch"
                component={WSSelect}
                componentProps={{
                  required: true,
                  tooltip: STATUS_TOOLTIP,
                  options: STATUS_OPTIONS
                }}
              />

              <WSInfoBox>
                <p>Reminders:</p>
                <ul>
                  <li>An example template is available for download</li>
                  <li>
                    Only “Paid (Historical upload)” can be associated with
                    archived contractors, all other statuses require the
                    contractors to be active at time of import.
                  </li>
                  <li>
                    If you created the batch by accident or are not ready to
                    upload the spreadsheet yet, you can delete the batch.
                  </li>
                </ul>
              </WSInfoBox>

              <ErrorAlert
                context="BulkBatchCreate"
                error={createBatchMeta.error}
              />
            </WSList>
          </WSModal>
        )}
      </WSForm.Values>
    </WSForm>
  );
};

export const useModalNewPayableBatch = () =>
  useWSModalCustom(ModalNewPayableBatch);
